import React from "react";
import { TextInput } from "flowbite-react";
import UserAvatar from "./UserAvatar";
import {
  HiOutlineBriefcase,
  HiOutlineDocumentText,
  HiOutlineEye,
  HiOutlineIdentification,
  HiOutlineLink,
} from "react-icons/hi";
import { useTranslation } from "react-i18next";
import HtmlEditor from "./HtmlEditor";
import { RICH_TEXT_LIMITS } from "../../utils/richText";
import { LinkItem, LinkManager } from "./LinkManager";
import BusinessCardFileUpload from "./BusinessCardFileUpload";
import BusinessCardRenderer from "./BusinessCardRenderer";
import {
  BusinessCardBooking,
  BusinessCardTemplate,
} from "../../types/BusinessCardTypes";
import type { AgendaType } from "../../hooks/agenda";
import BusinessCardBookingSection, {
  bookingTypeMissing,
} from "./BusinessCardBookingSection";
import { CustomButton } from "./CustomButton";
import { ModalShell } from "./ModalShell";
import FeatureUpgradePrompt from "./FeatureUpgradePrompt";

interface BusinessCardEditModalProps {
  show: boolean;
  onClose: () => void;
  selectedUser: {
    user_identification: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    avatar?: string;
    user_account_status?: string;
    hasBusinessCard?: boolean;
  } | null;
  businessCardForm: {
    jobTitle: string;
    description?: string;
    links: LinkItem[];
    uploadedFiles?: Array<{
      fileName: string;
      cdnPath: string;
      displayName: string;
    }>;
    allowVCardDownload?: boolean;
    booking?: BusinessCardBooking;
  };
  onFormChange: React.Dispatch<
    React.SetStateAction<BusinessCardEditModalProps["businessCardForm"]>
  >;
  onLinksSubmit: (data: { links: LinkItem[] }) => Promise<void>;
  onAssignBusinessCard: () => void;
  onResetForm: () => void;
  isLoading: boolean;
  customerIdentification: string;
  entityIdentification: string;
  descriptionEditorRef: React.RefObject<any>;
  isHtmlEmpty: (content: string) => boolean;
  entityPreferences?: {
    business_card_form?: {
      template?: BusinessCardTemplate;
      enableUserRegistration?: boolean;
      enableSaveContact?: boolean;
    };
    cdn_logo_project_path?: string;
  };
  entityData?: {
    entity_name?: string;
    entity_description?: string;
    entity_logo?: string;
  };
  hasCustomLinksAccess?: boolean;
  onUpgradeClick?: () => void;
  canAssign?: boolean;
  bookingTypes?: AgendaType[];
  canOfferBooking?: boolean;
  agendaOff?: boolean;
}

const BusinessCardEditModal: React.FC<BusinessCardEditModalProps> = ({
  show,
  onClose,
  selectedUser,
  businessCardForm,
  onFormChange,
  onLinksSubmit,
  onAssignBusinessCard,
  onResetForm,
  isLoading,
  customerIdentification,
  entityIdentification,
  descriptionEditorRef,
  isHtmlEmpty,
  entityPreferences,
  entityData,
  hasCustomLinksAccess = true,
  onUpgradeClick,
  canAssign = true,
  bookingTypes,
  canOfferBooking = false,
  agendaOff = false,
}) => {
  const { t } = useTranslation();
  const [descriptionValidationError, setDescriptionValidationError] =
    React.useState<string | undefined>();

  const booking = businessCardForm.booking;
  const typeMissing = bookingTypeMissing(booking, bookingTypes);

  const setBooking = (next: BusinessCardBooking) =>
    onFormChange({ ...businessCardForm, booking: next });

  const saveRefusal = !businessCardForm.jobTitle
    ? t("permissions.businessCard.editModal.validation.jobTitleRequired")
    : typeMissing
    ? t("permissions.businessCard.editModal.booking.typeMissing")
    : descriptionValidationError;

  const handleClose = () => {
    onClose();
    onResetForm();
  };

  const handleDescriptionValidation = (isValid: boolean, message?: string) => {
    setDescriptionValidationError(message);
  };

  return (
    <ModalShell
      show={show}
      onClose={handleClose}
      size="7xl"
      title={
        <span className="flex items-center gap-2 sm:gap-3">
          <HiOutlineIdentification className="text-blue-600 w-6 h-6 shrink-0" />
          <span className="min-w-0 truncate">
            {t("permissions.businessCard.editModal.title", {
              firstName: selectedUser?.first_name,
              lastName: selectedUser?.last_name,
            })}
          </span>
        </span>
      }
      subtitle={t("permissions.businessCard.editModal.subtitle")}
      footerClassName="justify-between"
      footer={
        <>
          {saveRefusal ? (
            <div className="flex items-center gap-2 text-sm text-amber-700 min-w-0">
              <span className="hidden sm:block w-4 h-4 rounded-full bg-amber-500 shrink-0" />
              <span className="font-medium" role="alert">
                {saveRefusal}
              </span>
            </div>
          ) : (
            <span />
          )}

          <div className="ml-auto flex gap-3">
            <CustomButton
              variant="secondary"
              onClick={handleClose}
              className="hidden sm:flex"
              title={t("common.cancel")}
            />
            <CustomButton
              onClick={onAssignBusinessCard}
              disabled={
                !canAssign ||
                !businessCardForm.jobTitle ||
                businessCardForm.jobTitle.length > 60 ||
                !!descriptionValidationError ||
                typeMissing ||
                isLoading
              }
              variant="primary"
              isLoading={isLoading}
              title={
                selectedUser?.hasBusinessCard
                  ? t("common.update")
                  : t("common.create")
              }
              icon="check"
            />
          </div>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_384px] gap-4 sm:gap-8 max-w-7xl mx-auto">
        <div className="order-2 lg:order-1">
          <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
            <section className="p-4 sm:p-6">
              <div className="grid grid-cols-[auto_1fr] gap-3 sm:gap-4 items-center">
                <div className="relative flex-shrink-0">
                  <UserAvatar
                    avatar={selectedUser?.avatar}
                    firstName={selectedUser?.first_name}
                    lastName={selectedUser?.last_name}
                    sizeClass="w-10 h-10 sm:w-16 sm:h-16"
                    textClass="text-base"
                  />
                  <div
                    className={`absolute bottom-0 right-0 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-white ${
                      selectedUser?.user_account_status?.toLowerCase() === "ok"
                        ? "bg-green-500"
                        : "bg-amber-500"
                    }`}
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                    {selectedUser?.first_name} {selectedUser?.last_name}
                  </h3>
                  <p
                    className="text-sm sm:text-base text-gray-600 truncate"
                    title={selectedUser?.email}
                  >
                    {selectedUser?.email}
                  </p>
                  {selectedUser?.phone && (
                    <p
                      className="text-xs sm:text-sm text-gray-500 truncate"
                      title={selectedUser.phone}
                    >
                      {selectedUser.phone}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="p-4 sm:p-6">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <HiOutlineBriefcase
                    size={18}
                    className="text-gray-400 flex-shrink-0"
                  />
                  <span>
                    {t("permissions.businessCard.editModal.jobTitle.heading")}
                  </span>
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {t("permissions.businessCard.editModal.jobTitle.description")}
                </p>
              </div>
              <TextInput
                value={businessCardForm.jobTitle}
                autoFocus
                onChange={(e) =>
                  onFormChange({
                    ...businessCardForm,
                    jobTitle: e.target.value,
                  })
                }
                placeholder={
                  t(
                    "permissions.businessCard.editModal.jobTitle.placeholder"
                  ) as string
                }
                required
                sizing="lg"
                className="text-lg"
                maxLength={60}
              />
              <div className="mt-2 grid grid-cols-[1fr_auto] gap-4 items-start">
                <div>
                  {!businessCardForm.jobTitle && (
                    <p className="text-sm text-red-600">
                      {t(
                        "permissions.businessCard.editModal.jobTitle.required"
                      )}
                    </p>
                  )}
                  {businessCardForm.jobTitle &&
                    businessCardForm.jobTitle.length > 60 && (
                      <p className="text-sm text-red-600">
                        {t(
                          "permissions.businessCard.editModal.jobTitle.tooLong"
                        )}
                      </p>
                    )}
                </div>
                <p
                  className={`text-xs ${
                    businessCardForm.jobTitle.length > 60
                      ? "text-red-500"
                      : businessCardForm.jobTitle.length > 50
                      ? "text-amber-500"
                      : "text-gray-500"
                  }`}
                >
                  {businessCardForm.jobTitle.length}/60
                </p>
              </div>
            </section>

            <section className="p-4 sm:p-6">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <HiOutlineDocumentText
                    size={18}
                    className="text-gray-400 flex-shrink-0"
                  />
                  <span>
                    {t(
                      "permissions.businessCard.editModal.description.heading"
                    )}
                  </span>
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {t("permissions.businessCard.editModal.description.subtitle")}
                </p>
              </div>
              <div className="border border-solid border-gray-200 rounded-lg bg-white">
                <HtmlEditor
                  ref={descriptionEditorRef}
                  value={businessCardForm.description}
                  onChange={(content) => {
                    const isEmpty = isHtmlEmpty(content || "");
                    const newDescription = isEmpty ? "" : content;
                    onFormChange((form) => ({
                      ...form,
                      description: newDescription,
                    }));
                  }}
                  onValidationChange={handleDescriptionValidation}
                  placeholder={
                    t(
                      "permissions.businessCard.editModal.description.placeholder"
                    ) as string
                  }
                  minHeight="150px"
                  maxHeight="250px"
                  maxLength={RICH_TEXT_LIMITS.businessCardDescription}
                  showCharacterCount={true}
                  formatting="basic"
                />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {t("permissions.businessCard.editModal.description.helperText")}
              </div>
            </section>

            <section className="p-4 sm:p-6">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <HiOutlineLink
                    size={18}
                    className="text-gray-400 flex-shrink-0"
                  />
                  <span>
                    {t("permissions.businessCard.editModal.links.heading")}
                  </span>
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {t("permissions.businessCard.editModal.links.subtitle")}
                </p>
                {hasCustomLinksAccess && (
                  <ul className="text-xs text-gray-500 space-y-1 mt-2">
                    <li>
                      •{" "}
                      {t(
                        "permissions.businessCard.editModal.links.examples.linkedin"
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "permissions.businessCard.editModal.links.examples.portfolio"
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "permissions.businessCard.editModal.links.examples.booking"
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "permissions.businessCard.editModal.links.examples.social"
                      )}
                    </li>
                  </ul>
                )}
              </div>

              {hasCustomLinksAccess ? (
                <div className="bg-white border border-gray-200 rounded-lg relative">
                  <LinkManager
                    data={businessCardForm.links}
                    showIconField={true}
                    showCustomIconUrl={true}
                    onSubmit={onLinksSubmit}
                    className="relative z-10"
                    maxLinks={5}
                  />
                </div>
              ) : (
                <FeatureUpgradePrompt
                  featureName={
                    t(
                      "permissions.businessCard.editModal.links.heading"
                    ) as string
                  }
                  featureDescription={
                    t(
                      "permissions.businessCard.editModal.links.upgradeDescription"
                    ) as string
                  }
                  benefits={[
                    t(
                      "permissions.businessCard.editModal.links.benefits.social"
                    ) as string,
                    t(
                      "permissions.businessCard.editModal.links.benefits.website"
                    ) as string,
                    t(
                      "permissions.businessCard.editModal.links.benefits.booking"
                    ) as string,
                  ]}
                  icon={HiOutlineLink}
                  iconTheme="blue"
                  onUpgrade={onUpgradeClick}
                />
              )}
            </section>

            <BusinessCardBookingSection
              booking={booking}
              bookingTypes={bookingTypes}
              canOfferBooking={canOfferBooking}
              agendaOff={agendaOff}
              holder={selectedUser}
              onChange={setBooking}
            />

            <section className="p-4 sm:p-6">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <HiOutlineDocumentText
                    size={18}
                    className="text-gray-400 flex-shrink-0"
                  />
                  <span>
                    {t("permissions.businessCard.editModal.files.heading")}
                  </span>
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {t("permissions.businessCard.editModal.files.subtitle")}
                </p>
                <ul className="text-xs text-gray-500 space-y-1 mt-2">
                  <li>
                    •{" "}
                    {t(
                      "permissions.businessCard.editModal.files.examples.brochures"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {t(
                      "permissions.businessCard.editModal.files.examples.pricing"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {t(
                      "permissions.businessCard.editModal.files.examples.portfolio"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {t(
                      "permissions.businessCard.editModal.files.examples.contact"
                    )}
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg">
                <BusinessCardFileUpload
                  onFileUploaded={(fileName, cdnPath, displayName) => {
                    const newFiles = [
                      ...(businessCardForm.uploadedFiles || []),
                      { fileName, cdnPath, displayName },
                    ];
                    onFormChange({
                      ...businessCardForm,
                      uploadedFiles: newFiles,
                    });
                  }}
                  onFileRemoved={(fileName) => {
                    const newFiles =
                      businessCardForm.uploadedFiles?.filter(
                        (file) => file.fileName !== fileName
                      ) || [];
                    onFormChange({
                      ...businessCardForm,
                      uploadedFiles: newFiles,
                    });
                  }}
                  uploadedFiles={businessCardForm.uploadedFiles || []}
                  maxFiles={5}
                  maxFileSizeMB={10}
                />
              </div>
            </section>
          </div>
        </div>

        <div className="order-1 lg:order-2 mb-6 lg:mb-0">
          <div className="lg:sticky lg:top-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg py-3 lg:py-4">
              <div className="mb-3 px-3">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <HiOutlineEye
                    size={18}
                    className="text-gray-400 flex-shrink-0"
                  />
                  <span>
                    {t("permissions.businessCard.editModal.preview.heading")}
                  </span>
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {t("permissions.businessCard.editModal.preview.subtitle")}
                </p>
              </div>

              {selectedUser ? (
                businessCardForm.jobTitle ? (
                  <div>
                    <BusinessCardRenderer
                      userData={{
                        user_identification: selectedUser.user_identification,
                        first_name: selectedUser.first_name,
                        last_name: selectedUser.last_name,
                        email: selectedUser.email,
                        phone: selectedUser.phone || "",
                        avatar: selectedUser.avatar || "",
                      }}
                      businessCardData={{
                        ...businessCardForm,
                        description: businessCardForm.description || "",
                      }}
                      customerIdentification={customerIdentification}
                      entityIdentification={entityIdentification}
                      className=""
                      fallbackTemplate="default"
                      isPreview={true}
                      entityPreferences={entityPreferences}
                      entityData={entityData}
                    />
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-gray-200">
                    <div className="text-gray-400 mb-4">
                      <HiOutlineEye size={40} className="mx-auto mb-3" />
                    </div>
                    <p className="text-gray-500 font-medium mb-1">
                      {t(
                        "permissions.businessCard.editModal.preview.placeholder"
                      )}
                    </p>
                    <p className="text-xs text-gray-400">
                      {t(
                        "permissions.businessCard.editModal.preview.jobTitleRequired"
                      )}
                    </p>
                  </div>
                )
              ) : (
                <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-gray-200">
                  <div className="text-gray-400 mb-4">
                    <HiOutlineEye size={40} className="mx-auto mb-3" />
                  </div>
                  <p className="text-gray-500 font-medium mb-1">
                    {t(
                      "permissions.businessCard.editModal.preview.placeholder"
                    )}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t("permissions.businessCard.editModal.preview.willAppear")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default BusinessCardEditModal;
